import type {
  PosAjustesDeLaFoto,
  PosEstado,
  PosEstadoDeLaFoto,
  PosEstadoDelAlmacen,
  PosEstadoDelMandato,
} from "@/platform/types";
import { PASARON_72_HORAS, cajerosQueEntran } from "./cajeroDeLaCaja";
import { motivoDelBloqueo } from "./estadoDeLaCaja";
import { progresoDeLaFoto } from "./fotoDeLaCaja";
import type { SituacionDelEquipo } from "./situacionDelEquipo";

/**
 * Vender sin red (#346 AC-1; §2.3, §2.5 y §2.7 de OFFLINE-POS-RONDA-2.md).
 * Bajo el mandato (sin red, o sin una sesión que pueda subir la venta) la caja
 * vende con la hoja de la foto, sin factura, con los medios y los topes del
 * mandato. Rust vuelve a controlar todo al registrar la venta. Lo que dice la
 * pantalla cuando no se puede es el texto del contrato.
 */

export const NO_VENDE_SIN_CONEXION = "Este negocio no vende sin conexión.";
export const SE_PREPARA_LA_CAJA = "Todavía se está preparando la caja.";
export const CARGA_TU_PIN = "Cargá tu PIN con conexión.";
export const COLA_ILEGIBLE =
  "La caja no puede vender sin conexión: avisale al dueño.";
export const SIN_CAJA_EN_EL_EQUIPO =
  "Este negocio todavía no tiene caja en este equipo.";
export const CAJA_DE_OTRO_LOCAL =
  "Este equipo es la caja de otro local: en éste no vende sin conexión.";
export const LLEGO_AL_TOPE = "Se llegó al tope de ventas sin conexión.";
export const SUPERA_EL_TOPE = "Esta venta supera el tope sin conexión.";
export const TODAVIA_NO_VENDE_SIN_CONEXION =
  "Esta caja todavía no vende sin conexión.";
export const SIN_FACTURA = "Sin conexión, esta venta sale sin factura.";
export const REQUIERE_CONEXION = "Requiere conexión";
export const MAYORISTA_PIDE_RED =
  "Un cliente mayorista requiere conexión: elegí el cliente de mostrador para vender sin conexión.";
export const CON_OBSERVACION =
  "Pasó más de un día sin conexión: lo que se venda va a llegar con una observación.";

/** Por qué no se vende sin red, con el detalle que suma la pantalla. */
export interface NoVendeSinRed {
  motivo: string;
  detalle?: string;
}

/** Un monto de la foto o del mandato, como número. `NaN` si no se lee. */
const monto = (texto: string | number | null | undefined) =>
  typeof texto === "number" ? texto : Number(texto ?? Number.NaN);

/**
 * A centavos, como core (`centavos`, `offline-sale.ts`): un total de 1000,004
 * no pasa un tope de 1000 si core lo redondea a 1000,00. `toFixed` antes de
 * redondear: 1.005 * 100 es 100.49999… en punto flotante.
 */
const centavos = (n: number) => Math.round(Number((n * 100).toFixed(6)));

/**
 * `maxSaleAmount` en `0` es como el negocio dice que no vende sin red (§2.3).
 * Un tope que no se lee tampoco deja vender. `null` es sin tope.
 */
export function elTopeNoDejaVender(maxSaleAmount: string | null): boolean {
  return maxSaleAmount !== null && !(monto(maxSaleAmount) > 0);
}

/** La primera carga de la foto empezó: alguna parte bajó o está bajando. */
const empezo = (foto: PosEstadoDeLaFoto) =>
  foto.bajando !== null ||
  foto.partes.some((p) => p.activa !== null || p.bajando !== null);

/** Los motivos del latido que descartan el mandato (§2.3 y §2.4). */
const RAZON_COMO_BLOQUEO: Record<string, PosEstado["bloqueo"]> = {
  terminal_deactivated: { tipo: "dada-de-baja" },
  business_suspended: { tipo: "suspendido" },
  no_es_esta_caja: { tipo: "no-es-esta-caja" },
};

/**
 * Por qué esta caja no puede vender sin red ahora (la tabla de §2.7), o `null`
 * si puede. Lo de una venta en particular (sus topes) lo dice
 * `porQueNoVendeEstaVenta`.
 */
export function porQueNoVendeSinRed({
  situacion,
  estado,
  foto,
  almacen,
  mandato,
  sinPermiso = null,
}: {
  situacion: SituacionDelEquipo["tipo"];
  estado: Pick<PosEstado, "bloqueo" | "razonSinMandato">;
  foto: PosEstadoDeLaFoto;
  almacen: PosEstadoDelAlmacen | null;
  mandato: PosEstadoDelMandato | null | undefined;
  /** El usuario de la sesión, si core le dijo `403 not_permitted` (§2.7). */
  sinPermiso?: string | null;
}): NoVendeSinRed | null {
  // Una cola apartada ya no se lee, pero la nueva sí: con la caja dada de alta
  // otra vez vende, y el aviso de que se apartó queda aparte (#359).
  if (
    almacen &&
    (!almacen.disponible || (almacen.colaApartada && situacion !== "caja"))
  ) {
    return { motivo: COLA_ILEGIBLE };
  }
  if (situacion === "sin-alta") return { motivo: SIN_CAJA_EN_EL_EQUIPO };
  if (situacion === "otro-local") return { motivo: CAJA_DE_OTRO_LOCAL };
  if (situacion === "dada-de-baja") {
    return { motivo: motivoDelBloqueo({ tipo: "dada-de-baja" }) };
  }

  // Lo que descarta el mandato no deja vender, ni con red ni sin red.
  const bloqueo = estado.bloqueo;
  if (bloqueo && bloqueo.tipo !== "sesion" && bloqueo.tipo !== "sin-permiso") {
    return { motivo: motivoDelBloqueo(bloqueo) };
  }
  if (foto.sinFoto) return { motivo: TODAVIA_NO_VENDE_SIN_CONEXION };

  const preparando = (): NoVendeSinRed => {
    const progreso = progresoDeLaFoto(foto);
    return progreso
      ? { motivo: SE_PREPARA_LA_CAJA, detalle: progreso }
      : { motivo: SE_PREPARA_LA_CAJA };
  };
  const vigente = mandato?.vigente?.mandato;
  if (!vigente) {
    const razon = estado.razonSinMandato;
    const descartado = razon ? RAZON_COMO_BLOQUEO[razon] : undefined;
    if (descartado) return { motivo: motivoDelBloqueo(descartado) };
    // Con un core de la ronda 1 la foto ni empieza: no se está preparando.
    if (razon === "no_sheet_yet" || (!foto.lista && empezo(foto))) {
      return preparando();
    }
    return { motivo: TODAVIA_NO_VENDE_SIN_CONEXION };
  }
  // La venta sin red se habilita recién con las cinco partes (#345 AC-7).
  if (!foto.lista) return preparando();
  if (mandato.vencido) return { motivo: PASARON_72_HORAS };

  const { maxSaleAmount, maxTotalAmount } = vigente.limits;
  if (elTopeNoDejaVender(maxSaleAmount)) {
    return { motivo: NO_VENDE_SIN_CONEXION };
  }
  if (
    maxTotalAmount !== null &&
    !(monto(mandato.vendidoSinRed) < monto(maxTotalAmount))
  ) {
    return { motivo: LLEGO_AL_TOPE };
  }
  if (cajerosQueEntran(mandato, sinPermiso).length === 0) {
    // Si sólo quedaba el usuario sin permiso, no es que falte un PIN.
    const habiaAlguno = cajerosQueEntran(mandato).length > 0;
    return {
      motivo: habiaAlguno
        ? motivoDelBloqueo({ tipo: "sin-permiso" })
        : CARGA_TU_PIN,
    };
  }
  return null;
}

/**
 * Si esta venta, con lo cobrado y sus recargos, pasa un tope del mandato
 * (§2.3): `maxSaleAmount` por venta, o `maxTotalAmount` con lo ya vendido bajo
 * el mandato desde el último latido que lo renovó. Los montos se comparan como
 * número; un tope que no se lee no deja vender.
 */
export function porQueNoVendeEstaVenta(
  total: string | number,
  mandato: PosEstadoDelMandato | null | undefined
): string | null {
  const vigente = mandato?.vigente?.mandato;
  if (!vigente) return TODAVIA_NO_VENDE_SIN_CONEXION;
  const cobrado = monto(total);
  const { maxSaleAmount, maxTotalAmount } = vigente.limits;
  if (elTopeNoDejaVender(maxSaleAmount)) return NO_VENDE_SIN_CONEXION;
  if (
    maxSaleAmount !== null &&
    !(centavos(cobrado) <= centavos(monto(maxSaleAmount)))
  ) {
    return SUPERA_EL_TOPE;
  }
  if (
    maxTotalAmount !== null &&
    !(
      centavos(monto(mandato.vendidoSinRed)) + centavos(cobrado) <=
      centavos(monto(maxTotalAmount))
    )
  ) {
    return LLEGO_AL_TOPE;
  }
  return null;
}

/**
 * Entre las 24 h y las 72 h se sigue vendiendo, y la caja avisa que las ventas
 * van a llegar con observación (§2.7).
 */
export function avisoDeLaVentana(
  mandato: PosEstadoDelMandato | null | undefined,
  ahora = new Date()
): string | null {
  const vigente = mandato?.vigente?.mandato;
  if (!vigente || mandato.vencido) return null;
  const desde = new Date(vigente.softUntil).getTime();
  return Number.isFinite(desde) && ahora.getTime() >= desde
    ? CON_OBSERVACION
    : null;
}

/** La venta a un cliente mayorista pide red (#346, «Fuera de la versión 1»). */
export const esClienteMayorista = (c: { buyerType: string }) =>
  c.buyerType === "wholesaler";

export type MedioSinRed = NonNullable<
  PosAjustesDeLaFoto["paymentMethods"]
>[number];

/**
 * Los medios que valen sin red: los de la foto que el tipo deja usar sin red
 * (`allowedOffline`) y que el mandato trae en `paymentMethodIds` (§2.1 y §2.3).
 */
export function mediosSinRed(
  ajustes: PosAjustesDeLaFoto | null | undefined,
  mandato: PosEstadoDelMandato | null | undefined
): MedioSinRed[] {
  const delMandato = new Set(mandato?.vigente?.mandato.paymentMethodIds ?? []);
  return (ajustes?.paymentMethods ?? []).filter(
    (m) => m.allowedOffline && delMandato.has(m.id)
  );
}

/**
 * El negocio factura (`hasElectronicBilling`, o `invoicingMode` distinto de
 * `disabled`): sin red, la venta sale sin factura y la caja lo avisa (§2.7).
 */
export function facturaElNegocio(
  ajustes: PosAjustesDeLaFoto | null | undefined
): boolean {
  const negocio = ajustes?.business;
  return (
    !!negocio?.hasElectronicBilling ||
    (!!negocio?.invoicingMode && negocio.invoicingMode !== "disabled")
  );
}
